import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { CountryEntry, CountryTax, PhonePrefix } from '../lib/countries';
import {
    coerceCountries,
    findCountry,
    phonePrefixesFrom,
    selectableCountries,
    taxesFor,
} from '../lib/countryRegistryRules';

/**
 * The platform's country registry (`platform_settings.country_registry`), read
 * by every country picker, phone-prefix selector and tax-id / tax list in both
 * SPAs.
 *
 * A module-level store, NOT a provider: each app registers how to fetch it once
 * at bootstrap (`setCountryRegistryLoader` — the tenant and the admin reach the
 * registry through different endpoints) and any component calls the hook. The
 * FIRST mounted consumer triggers the fetch; concurrent consumers share the
 * in-flight request, and every mounted consumer re-renders when it lands (same
 * shape as the tenant's `useCostConfig`, on `useSyncExternalStore`).
 *
 * Until the list arrives `countries` is `[]` and `loading` is true. Consumers
 * MUST NOT treat that empty list as "this country does not exist" — a selector
 * that resets its value, or a form that backfills a default, while `loading` is
 * true would rewrite stored data on a slow network.
 *
 * A failed fetch is not cached: the next consumer to mount (or `reload()`)
 * tries again.
 */

type Loader = () => Promise<{ countries: CountryEntry[] }>;

interface StoreState {
    /** `null` = never loaded. */
    countries: CountryEntry[] | null;
    error: unknown;
}

let loader: Loader | null = null;
let state: StoreState = { countries: null, error: null };
/** The loaded list no longer reflects the server (an admin just saved). It is
 *  still served while the refetch runs, so pickers don't blank out. */
let stale = false;
let inflight: Promise<void> | null = null;
/** Bumped by `invalidateCountryRegistry`, so a response already in flight when
 *  the registry changed cannot write the pre-save list back afterwards. */
let epoch = 0;
const subscribers = new Set<() => void>();

const EMPTY: CountryEntry[] = [];

function setState(next: StoreState): void {
    state = next;
    subscribers.forEach((fn) => fn());
}

function subscribe(fn: () => void): () => void {
    subscribers.add(fn);
    return () => {
        subscribers.delete(fn);
    };
}

function getSnapshot(): StoreState {
    return state;
}

function load(): Promise<void> {
    if (state.countries && !stale) return Promise.resolve();
    if (inflight) return inflight;
    if (!loader) {
        // A wiring bug, not a network one: say so instead of spinning forever.
        if (!state.error) {
            setState({ ...state, error: new Error('country registry loader not registered') });
        }
        return Promise.resolve();
    }
    const started = epoch;
    // A retry after a failure is loading again, not failed.
    if (state.error) setState({ ...state, error: null });
    const p = loader()
        .then(
            (res) => {
                if (started !== epoch) return;
                stale = false;
                setState({ countries: coerceCountries(res?.countries), error: null });
            },
            (err: unknown) => {
                if (started !== epoch) return;
                setState({ ...state, error: err ?? new Error('country registry load failed') });
            },
        )
        .finally(() => {
            if (inflight === p) inflight = null;
        });
    inflight = p;
    return p;
}

/** Register how this app fetches the registry. Call it once at bootstrap. */
export function setCountryRegistryLoader(next: () => Promise<{ countries: CountryEntry[] }>): void {
    loader = next;
    if (state.error && !state.countries) {
        // A consumer mounted before registration recorded the wiring error;
        // clear it and fetch for whoever is already waiting.
        setState({ countries: null, error: null });
        if (subscribers.size > 0) void load();
    }
}

/**
 * Mark the registry as changed (the admin calls it after a successful save).
 * Mounted consumers keep the list they have until the fresh one lands; with no
 * consumer mounted, the next one to mount fetches.
 */
export function invalidateCountryRegistry(): void {
    epoch += 1;
    inflight = null;
    stale = true;
    if (state.error) setState({ ...state, error: null });
    if (subscribers.size > 0) void load();
}

export interface CountryRegistry {
    /** Every entry, in registry order. `[]` until loaded. */
    countries: CountryEntry[];
    /** The entries shown in country pickers, in registry order. */
    selectable: CountryEntry[];
    /** One option per dial-in code (first country in the list owns the flag). */
    phonePrefixes: PhonePrefix[];
    /** True until the first list arrives (and no error has been recorded). */
    loading: boolean;
    /** The last load error while no list is available, else `null`. */
    error: unknown;
    find(code?: string | null): CountryEntry | undefined;
    taxesFor(code?: string | null): CountryTax[];
    /** Refetch now (keeps serving the current list meanwhile). */
    reload(): Promise<void>;
}

export function useCountryRegistry(): CountryRegistry {
    const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

    useEffect(() => {
        void load();
    }, []);

    const countries = snap.countries ?? EMPTY;
    const loading = snap.countries === null && !snap.error;
    const error = snap.countries === null ? snap.error : null;

    const selectable = useMemo(() => selectableCountries(countries), [countries]);
    const phonePrefixes = useMemo(() => phonePrefixesFrom(countries), [countries]);

    const find = useCallback((code?: string | null) => findCountry(countries, code), [countries]);
    const taxes = useCallback((code?: string | null) => taxesFor(countries, code), [countries]);
    const reload = useCallback(async () => {
        stale = true;
        await load();
    }, []);

    return {
        countries,
        selectable,
        phonePrefixes,
        loading,
        error,
        find,
        taxesFor: taxes,
        reload,
    };
}
